// Разбор гардероба: тонкий слой над /api/declutter. Логики здесь нет — только запросы и типы,
// зеркалящие DTO backend (зеркало service/StylistApi.ts).
import createAxiosInstance from '../support/AxiosSupport';
import {DisposalIntent} from '../support/itemLifecycle';

export type DeclutterAction =
    | 'KEEP' | 'SELL' | 'DONATE' | 'GIVE_AWAY' | 'DISPOSE' | 'MOVE_TO_HOME' | 'MOVE_TO_WORK';

export interface DeclutterProgress {
    targetCount: number;
    decidedForRemoval: number;
    breakdown: Record<string, number>;
    keptCount: number;
    deferredCount: number;
    remainingCandidates: number;
    targetReached: boolean;
}

export interface DeclutterCategoryGoal {
    categoryId: string | null;
    categoryName: string | null;
    activeCount: number;
    candidateCount: number;
    targetCount: number;
    decidedForRemoval: number;
    status: string;
}

export interface DeclutterSession {
    id: string;
    status: string;
    mode: string;
    grayZonePolicy: string;
    quick: boolean;
    startedAt: string | null;
    decisionsCompletedAt: string | null;
    completedAt: string | null;
    progress: DeclutterProgress;
    categories: DeclutterCategoryGoal[];
    /** Льгота новизны сессии, дней; null у сессий, созданных до фичи. */
    newGraceDays?: number | null;
}

export interface DeclutterOverview {
    activeCount: number;
    candidateCount: number;
    /** 0 при duplicatesNotComputed — состояние читать из флага, не из числа. */
    duplicateGroupCount: number;
    duplicatesNotComputed?: boolean;
    duplicatesUnindexedCount?: number;
    notWornOverYearCount: number;
    categories: DeclutterCategoryGoal[];
    problemZones: string[];
    openSession: DeclutterSession | null;
    /**
     * Последняя сессия любого статуса. После «Завершить решения» открытой сессии нет, а отчёт и
     * физический шаг относятся именно к ней. Additive-поле: старый бэкенд его не отдаёт.
     */
    lastSession?: DeclutterSession | null;
    quickAvailableCount: number;
    /** Льгота, при которой посчитан ответ; additive-поля — старый бэкенд их не отдаёт. */
    newGraceDays?: number;
    defaultNewGraceDays?: number;
    maxNewGraceDays?: number;
}

export interface DeclutterQueueItem {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    score: number | null;
    lastWornAt: string | null;
    conditionName: string | null;
    priorityBucket: string;
    recommendedAction: DeclutterAction | null;
    decidedAction: DeclutterAction | null;
    status: string;
    rank: number;
}

export interface DeclutterFactor {
    code: string;
    label: string;
    impact: number;
    known: boolean;
}

export interface DeclutterItemCard {
    itemId: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    size: string | null;
    primaryPhotoUrl: string | null;
    conditionName: string | null;
    totalWears: number | null;
    lastWornAt: string | null;
    priceRub: string | null;
    score: number | null;
    confidence: number | null;
    recommendedAction: DeclutterAction | null;
    decidedAction: DeclutterAction | null;
    factors: DeclutterFactor[];
    reasons: string[];
    priorityBucket: string;
    grayZone: boolean;
    stale: boolean;
    /**
     * Краткое объяснение человеческим языком — 2–3 причины вместо списка слагаемых.
     * Additive-поле пакета B: без него карточка берёт первые причины и факторы сама.
     */
    summaryReasons?: string[] | null;
    /**
     * Подпись основного действия («Утилизировать», «Проверить» в серой зоне). Additive-поле
     * пакета B; backend отдаёт текст, а код действия — в {@link DeclutterItemCard#recommendedAction}.
     */
    primaryAction?: DeclutterAction | string | null;
    /**
     * Как называется интегральный балл. «Состояние» — только физическое состояние вещи
     * (аудит 17.09, D04); additive-поле, без него подпись берётся по умолчанию.
     */
    scoreLabel?: string | null;
    /** Когда посчитана оценка — именно её обновляет «Обновить оценку» (D03). */
    assessmentComputedAt?: string | null;
    /** Физическое состояние и ремонтопригодность — отдельно от балла полезности (D04). */
    physicalCondition?: ItemPhysicalCondition | null;
}

/** Физическое состояние вещи: что с ней не так и чинится ли это (аудит 17.09, D04). */
export interface ItemPhysicalCondition {
    conditionName: string | null;
    defectCount: number;
    significantDefects: number;
    worstSeverity: string | null;
    repairability: string | null;
    summary: string;
    /** Повреждения не выводят вещь из носки. */
    wearable: boolean;
}

/** Снимок оценки для диффа «было → стало» (аудит 17.09, D03). */
export interface AssessmentSnapshot {
    score: number | null;
    action: DeclutterAction | null;
    actionLabel: string | null;
    summaryReasons: string[];
}

/** Результат пересчёта оценки вещи (аудит 17.09, D03). */
export interface DeclutterReassessment {
    computedAt: string | null;
    changed: boolean;
    before: AssessmentSnapshot;
    after: AssessmentSnapshot;
    card: DeclutterItemCard;
}

/** Вид попытки в ветке «Сначала попробовать» (аудит 17.09, D10). */
export type RestorationKind = 'REPAIR' | 'CLEAN' | 'ALTER' | 'TRIAL';

export interface DecisionResult {
    progress: DeclutterProgress;
    nextItemId: string | null;
    appliedAction: string | null;
}

const ownerParams = (ownerId: string | null) => (ownerId ? {ownerId} : {});

export async function fetchOverview(ownerId: string | null,
                                    newGraceDays?: number): Promise<DeclutterOverview> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterOverview>('/declutter/overview', {
        params: {...ownerParams(ownerId), ...(newGraceDays != null ? {newGraceDays} : {})},
    })).data;
}

export async function startSession(ownerId: string | null, mode: string, quick: boolean,
                                   grayZonePolicy?: string,
                                   newGraceDays?: number): Promise<DeclutterSession> {
    const api = await createAxiosInstance();
    return (await api.post<DeclutterSession>('/declutter/sessions',
        {ownerId, mode, quick, grayZonePolicy, newGraceDays})).data;
}

export async function fetchQueue(ownerId: string | null, sessionId: string,
                                 categoryId?: string): Promise<DeclutterQueueItem[]> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterQueueItem[]>(`/declutter/sessions/${sessionId}/queue`,
        {params: {...ownerParams(ownerId), ...(categoryId ? {categoryId} : {})}})).data;
}

export async function fetchCard(ownerId: string | null, sessionId: string,
                                itemId: string): Promise<DeclutterItemCard> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterItemCard>(
        `/declutter/sessions/${sessionId}/items/${itemId}/card`, {params: ownerParams(ownerId)})).data;
}

/**
 * Решение по вещи. `acceptOutdated` — явное «решаю по текущим данным»: без него backend
 * отклоняет решение по устаревшей оценке (409 `ASSESSMENT_OUTDATED`, аудит 17.09, D03).
 */
export async function recordDecision(ownerId: string | null, sessionId: string, itemId: string,
                                     action: DeclutterAction,
                                     acceptOutdated = false): Promise<DecisionResult> {
    const api = await createAxiosInstance();
    return (await api.post<DecisionResult>(
        `/declutter/sessions/${sessionId}/items/${itemId}/decision`, {action, acceptOutdated},
        {params: ownerParams(ownerId)})).data;
}

/** Пересчёт оценки вещи: возвращает время расчёта и дифф «было → стало» (D03). */
export async function reassessItem(ownerId: string | null, sessionId: string,
                                   itemId: string): Promise<DeclutterReassessment> {
    const api = await createAxiosInstance();
    return (await api.post<DeclutterReassessment>(
        `/declutter/sessions/${sessionId}/items/${itemId}/reassess`, {},
        {params: ownerParams(ownerId)})).data;
}

/** «Сначала попробовать»: вещь уходит на восстановление или проверку до даты (D10). */
export async function tryFirst(ownerId: string | null, sessionId: string, itemId: string,
                               kind: RestorationKind, dueDate?: string | null,
                               note?: string | null): Promise<DecisionResult> {
    const api = await createAxiosInstance();
    return (await api.post<DecisionResult>(
        `/declutter/sessions/${sessionId}/items/${itemId}/try-first`,
        {kind, dueDate: dueDate ?? null, note: note ?? null},
        {params: ownerParams(ownerId)})).data;
}

/**
 * Отмена решения по вещи (Q13): возвращает её в очередь и вынимает из корзины, если
 * решение уже увело её туда. Тот же эндпоинт, что и у кнопки «Отменить» в тосте.
 */
export async function undoDecision(ownerId: string | null, sessionId: string,
                                   itemId: string): Promise<DecisionResult> {
    const api = await createAxiosInstance();
    return (await api.delete<DecisionResult>(
        `/declutter/sessions/${sessionId}/items/${itemId}/decision`,
        {params: ownerParams(ownerId)})).data;
}

export async function completeSession(ownerId: string | null, sessionId: string): Promise<DeclutterSession> {
    const api = await createAxiosInstance();
    return (await api.post<DeclutterSession>(`/declutter/sessions/${sessionId}/complete`, {},
        {params: ownerParams(ownerId)})).data;
}

/** Страница истории разборов (Spring `Page`): нужны только элементы и общий счётчик. */
export interface DeclutterHistoryPage {
    content: DeclutterSession[];
    totalElements: number;
}

export async function fetchHistory(ownerId: string | null, page = 0,
                                   size = 10): Promise<DeclutterHistoryPage> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterHistoryPage>('/declutter/sessions',
        {params: {...ownerParams(ownerId), page, size}})).data;
}

/**
 * Код ошибки backend (`GlobalExceptionHandler` кладёт `title` ApiException в поле `error`).
 * Нужен странице, чтобы отличить «уже есть открытый разбор» от «гардероб пуст».
 */
export function apiErrorCode(e: unknown): string | null {
    const data = (e as {response?: {data?: {error?: unknown}}})?.response?.data;
    return typeof data?.error === 'string' ? data.error : null;
}

export interface DeclutterPendingItem {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    categoryName: string | null;
    /** Размер: у шкафа вещь опознают по приметам, а не по названию. Additive-поле. */
    sizeName?: string | null;
    /** Записанное намерение — оно же способ выбытия по умолчанию. Additive-поле. */
    intent?: DisposalIntent | null;
    bucket: string;
    heldDays: number | null;
    /** Откуда взялось намерение: решение разбора или ручной перенос. Additive-поле. */
    decisionSource?: string | null;
}

export interface DeclutterPhysicalStep {
    countsByBucket: Record<string, number>;
    items: DeclutterPendingItem[];
    oldestHeldDays: number | null;
}

export interface DeclutterReport {
    stage: 'DECISIONS' | 'FINAL';
    decisions: Record<string, number>;
    outcomes: Record<string, number>;
    pendingCount: number;
    freedCubicMeters: string | number | null;
    freedKilograms: string | number | null;
    estimated: boolean;
    before: Record<string, unknown>;
    after: Record<string, unknown>;
}

export async function fetchPending(ownerId: string | null, bucket?: string): Promise<DeclutterPhysicalStep> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterPhysicalStep>('/declutter/pending',
        {params: {...ownerParams(ownerId), ...(bucket ? {bucket} : {})}})).data;
}

/**
 * Подтверждение факта выбытия. `method` — что произошло на самом деле (без него берётся
 * записанное намерение), `disposalDate` — когда (без неё сегодня). Повтор по уже выбывшей
 * вещи не ошибка: backend её пропускает.
 */
export async function confirmDisposal(ownerId: string | null, itemIds: string[],
                                      method?: DisposalIntent,
                                      disposalDate?: string): Promise<number> {
    const api = await createAxiosInstance();
    return (await api.post<number>('/declutter/pending/confirm',
        {ownerId, itemIds, method, disposalDate})).data;
}

export async function returnToWardrobe(ownerId: string | null, itemIds: string[]): Promise<number> {
    const api = await createAxiosInstance();
    return (await api.post<number>('/declutter/pending/return', {ownerId, itemIds})).data;
}

export async function fetchReport(ownerId: string | null, sessionId: string): Promise<DeclutterReport> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterReport>(`/declutter/sessions/${sessionId}/report`,
        {params: ownerParams(ownerId)})).data;
}

export interface SaleMarketplace {
    id: string;
    code: string;
    name: string;
    urlTemplate: string | null;
}

export interface SaleListingPlacement {
    id: string;
    marketplaceId: string;
    marketplaceName: string | null;
    externalUrl: string | null;
    postedAt: string | null;
    note: string | null;
}

export interface SaleListing {
    id: string;
    itemId: string;
    itemName: string | null;
    primaryPhotoUrl: string | null;
    status: string;
    title: string | null;
    description: string | null;
    conditionText: string | null;
    suggestedPriceRub: string | number | null;
    priceRangeFromRub: string | number | null;
    priceRangeToRub: string | number | null;
    askingPriceRub: string | number | null;
    soldPriceRub: string | number | null;
    soldAt: string | null;
    unsoldResolution: string | null;
    generatedBy: string | null;
    priceKnown: boolean;
    placements: SaleListingPlacement[];
}

export async function fetchMarketplaces(ownerId: string | null): Promise<SaleMarketplace[]> {
    const api = await createAxiosInstance();
    return (await api.get<SaleMarketplace[]>('/declutter/sales/marketplaces',
        {params: ownerParams(ownerId)})).data;
}

export async function fetchListings(ownerId: string | null, activeOnly = true): Promise<SaleListing[]> {
    const api = await createAxiosInstance();
    return (await api.get<SaleListing[]>('/declutter/sales',
        {params: {...ownerParams(ownerId), activeOnly}})).data;
}

export async function createListing(ownerId: string | null, itemId: string): Promise<SaleListing> {
    const api = await createAxiosInstance();
    return (await api.post<SaleListing>('/declutter/sales', {},
        {params: {...ownerParams(ownerId), itemId}})).data;
}

export async function regenerateListing(ownerId: string | null, listingId: string): Promise<SaleListing> {
    const api = await createAxiosInstance();
    return (await api.post<SaleListing>(`/declutter/sales/${listingId}/regenerate`, {},
        {params: ownerParams(ownerId)})).data;
}

export async function addPlacement(ownerId: string | null, listingId: string,
                                   marketplaceId: string, externalUrl?: string): Promise<SaleListing> {
    const api = await createAxiosInstance();
    return (await api.post<SaleListing>(`/declutter/sales/${listingId}/placements`,
        {ownerId, marketplaceId, externalUrl})).data;
}

export async function markSold(ownerId: string | null, listingId: string,
                               soldPriceRub: number): Promise<SaleListing> {
    const api = await createAxiosInstance();
    return (await api.post<SaleListing>(`/declutter/sales/${listingId}/sold`,
        {ownerId, soldPriceRub})).data;
}

export async function markNotSold(ownerId: string | null, listingId: string,
                                  resolution: string): Promise<SaleListing> {
    const api = await createAxiosInstance();
    return (await api.post<SaleListing>(`/declutter/sales/${listingId}/not-sold`,
        {ownerId, resolution})).data;
}

export interface DuelSide {
    itemId: string;
    name: string | null;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    categoryName: string | null;
    conditionName: string | null;
    totalWears: number | null;
    score: number | null;
}

export interface DuelCriterion {
    code: string;
    label: string;
    winner: 'A' | 'B' | 'TIE' | 'UNKNOWN';
    valueA: string | null;
    valueB: string | null;
}

export interface DuelImpact {
    compatibilityDeltaPercent: number | null;
    duplicatePairsRemoved: number;
    freedCubicMeters: string | number | null;
    freedKilograms: string | number | null;
    estimated: boolean;
}

export interface DeclutterDuel {
    id: string;
    itemA: DuelSide | null;
    itemB: DuelSide | null;
    winnerItemId: string | null;
    confidence: number;
    tie: boolean;
    criteria: DuelCriterion[];
    impact: DuelImpact | null;
    resolution: string | null;
}

export async function generateDuels(ownerId: string | null, sessionId: string): Promise<number> {
    const api = await createAxiosInstance();
    return (await api.post<number>(`/declutter/sessions/${sessionId}/duels/generate`, {},
        {params: ownerParams(ownerId)})).data;
}

/** Пары закончились — backend отвечает 204, и это не ошибка. */
export async function fetchNextDuel(ownerId: string | null,
                                    sessionId: string): Promise<DeclutterDuel | null> {
    const api = await createAxiosInstance();
    const response = await api.get<DeclutterDuel | ''>(
        `/declutter/sessions/${sessionId}/duels/next`, {params: ownerParams(ownerId)});
    return response.status === 204 || !response.data ? null : (response.data as DeclutterDuel);
}

export async function resolveDuel(ownerId: string | null, sessionId: string, duelId: string,
                                  resolution: string): Promise<DeclutterDuel> {
    const api = await createAxiosInstance();
    return (await api.post<DeclutterDuel>(
        `/declutter/sessions/${sessionId}/duels/${duelId}/resolve`, {},
        {params: {...ownerParams(ownerId), resolution}})).data;
}

export interface DeclutterObjection {
    id: string;
    objectionCode: string;
    objectionLabel: string;
    answer: string;
    revisedAction: string | null;
    llmUsed: boolean;
    limitReached: boolean;
    createdAt: string | null;
}

export async function answerObjection(ownerId: string | null, sessionId: string, itemId: string,
                                      objectionCode: string): Promise<DeclutterObjection> {
    const api = await createAxiosInstance();
    return (await api.post<DeclutterObjection>(
        `/declutter/sessions/${sessionId}/items/${itemId}/objections`, {objectionCode},
        {params: ownerParams(ownerId)})).data;
}

export async function fetchObjections(ownerId: string | null, sessionId: string,
                                      itemId: string): Promise<DeclutterObjection[]> {
    const api = await createAxiosInstance();
    return (await api.get<DeclutterObjection[]>(
        `/declutter/sessions/${sessionId}/items/${itemId}/objections`,
        {params: ownerParams(ownerId)})).data;
}
